"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { parseMarkdownToHtml } from "@/lib/markdown";
import { formatDate } from "@/lib/datetime";

interface LegalDoc {
  key: string;
  title: string;
  content: string;
  updated_at: string;
}

export default function PrivacyPolicyPage() {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/legal/privacy_policy")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load privacy policy.");
        return res.json();
      })
      .then((data) => {
        setDoc(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // The document arrives after mount, so the browser's own jump to `#cookies`
  // finds nothing at load time. Repeat it once the content exists.
  useEffect(() => {
    if (!doc || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    el?.scrollIntoView();
  }, [doc]);

  const renderedHtml = doc ? parseMarkdownToHtml(doc.content) : "";
  const lastUpdated = doc
    ? formatDate(doc.updated_at)
    : "Loading...";

  return (
    <div className="bg-canvas min-h-screen py-16 px-6 md:px-8 relative overflow-hidden">
      {/* 🔮 Background Light Ray Glow */}
      <div className="absolute top-0 left-1/4 h-[400px] w-[400px] rounded-full bg-blue-500/5 blur-[100px]" />
      
      <div className="max-w-3xl mx-auto relative z-10 space-y-8">
        <div className="border-b border-divider pb-6">
          <Link href="/" className="text-xs font-mono tracking-wider text-brand-primary uppercase hover:underline">
            ← Return to Storefront
          </Link>
          <h1 className="text-3xl font-black text-primary tracking-tight mt-3">
            {doc?.title || "Privacy Policy"}
          </h1>
          <p className="text-xs text-secondary mt-1 font-mono">
            Last updated: {lastUpdated}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-3">
            <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent animate-spin rounded-full" />
            <p className="text-xs text-secondary font-mono">Retrieving active policy guidelines...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
            <p className="text-sm text-red-450 font-mono">Error loading Privacy Policy: {error}</p>
          </div>
        ) : (
          <div 
            className="space-y-6 text-sm text-secondary leading-relaxed font-sans prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}
      </div>
    </div>
  );
}